import { EditorProps } from "@monaco-editor/react";
import { Base64 } from "js-base64";
import { useCallback, useMemo } from "react";

import CodeEditor from "components/CodeEditor";
import Box from "ds/components/Box";
import useAnalytics from "hooks/useAnalytics";
import { EntityAttributeChange, EntityChangeType } from "types/generated";

import { showJsonDiffModal } from "./JsonDiffModal";
import { showJsonModal } from "./JsonModal";
import {
  encodeId,
  generateEntityChangeValue,
  generateNestedObjectBasedOnJsonPath,
  getPrettyPrintedResources,
} from "./helpers";
import { useJsonCodeLenses } from "./hooks";
import styles from "./styles.module.css";
import { EncodedJsonValue, EncodedJsonValueTuple } from "./types";

type RunChangesResourcesDiffProps = {
  items: EntityAttributeChange[];
  changeType: EntityChangeType;
  editorHeight: number;
  setEditorHeight: (height: number) => unknown;
  id: string;
};

const monacoOptions: EditorProps["options"] = {
  lineNumbers: "off",
  glyphMargin: false,
  unicodeHighlight: {
    invisibleCharacters: false,
  },
};

const RunChangesResourcesDiff = ({
  editorHeight,
  items,
  changeType,
  setEditorHeight,
  id,
}: RunChangesResourcesDiffProps) => {
  const trackEvent = useAnalytics();

  const changesArray = useMemo(
    () =>
      items.map((item) => {
        return {
          name: item.path,
          value: generateEntityChangeValue(changeType, item.changeForPhase),
          type: item.changeForPhase.metadata.type,
        };
      }),
    [items, changeType]
  );

  const resourceChanges = useMemo(
    () => generateNestedObjectBasedOnJsonPath(changesArray),
    [changesArray]
  );

  const resourcesChangesOutcome = useMemo(
    () => getPrettyPrintedResources(resourceChanges, changeType),
    [resourceChanges, changeType]
  );

  let resourcesChangeText = "Not possible to display.";

  if (resourcesChangesOutcome.result.length) {
    resourcesChangeText = resourcesChangesOutcome.result.trimEnd();
  }

  const handleJsonDiffModalOpen = useCallback(
    (value: EncodedJsonValueTuple) => {
      trackEvent("JSON diff modal opened");
      showJsonDiffModal({
        value,
        onClose: () => trackEvent("JSON diff modal closed"),
      });
    },
    [trackEvent]
  );

  const handleOnJsonModalOpen = useCallback(
    (value: EncodedJsonValue) => {
      trackEvent("JSON view modal opened");
      showJsonModal({ value, onClose: () => trackEvent("JSON view modal closed") });
    },
    [trackEvent]
  );

  const encodedId = encodeId(id);

  const onEditorMount = useJsonCodeLenses({
    id: encodedId,
    jsonLocations: resourcesChangesOutcome.jsonLocations,
    onJsonDiffModalOpen: handleJsonDiffModalOpen,
    onJsonModalOpen: handleOnJsonModalOpen,
  });

  return (
    <Box
      direction="column"
      grow="1"
      fullWidth
      relative
      className={styles.details}
      style={{ height: editorHeight }}
    >
      <CodeEditor
        body={resourcesChangeText}
        language="resourcesDiff"
        className={styles.editor}
        handleHeightCallback={setEditorHeight}
        path={Base64.encodeURI(id)}
        options={monacoOptions}
        readOnly
        onMountCallback={onEditorMount}
        skeletonCount={3}
      />
    </Box>
  );
};

export default RunChangesResourcesDiff;
